import type { FirestoreValue } from '@elara/firebolt/shared';

import { Signal } from '@preact/signals-react';
import { Timestamp } from 'firebase/firestore';
import { mapValues } from 'lodash-es';

import { objectKey } from '@elara/core/introspection';

/**
 * Duck-types a Firestore `Timestamp` across SDKs (JS client, React Native native, admin).
 *
 * All three expose the same `{ seconds, nanoseconds }` pair plus `toDate()`, which is the contract
 * firebolt's schemas already rely on — and the only thing this module needs to know to pass a
 * timestamp through untouched.
 */
function isFirestoreTimestamp(value: unknown): boolean {
  // Null first, then the object test: `typeof null === 'object'`, so the order matters for the guard
  // to read as intended (and keeps static analysis from mistaking the null check for dead code).
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as { seconds?: unknown; nanoseconds?: unknown; toDate?: unknown };
  return (
    typeof candidate.seconds === 'number' &&
    typeof candidate.nanoseconds === 'number' &&
    typeof candidate.toDate === 'function'
  );
}

/**
 * @internal
 * @function valueToFirestore
 * Recursively serializes an application-level value into a format compatible with Firestore.
 * Handles Dates, Timestamps, Signals, and nested objects/arrays.
 * @param appValue - The value from the application model.
 * @returns The Firestore-compatible value.
 */
export function valueToFirestore(appValue: unknown): FirestoreValue {
  if (appValue === null || appValue === undefined) return null;
  if (typeof appValue === 'string') return appValue;
  if (typeof appValue === 'number') return appValue;
  if (typeof appValue === 'boolean') return appValue;
  if (appValue instanceof Date) return Timestamp.fromDate(appValue);
  // Structural, not `instanceof`: mobile runs `@react-native-firebase/firestore`, whose Timestamp is a
  // DIFFERENT class from the JS SDK's, so an `instanceof` check silently reported `false` for native
  // timestamps and fell through to the object branch below — which would `mapValues` a Timestamp into
  // a plain `{seconds, nanoseconds}` bag. Firebolt already treats Firestore timestamps structurally
  // (`FirestoreTimestamp` in `zod-types.ts`, "works with both client and admin SDKs"); match it here.
  if (isFirestoreTimestamp(appValue)) return appValue as FirestoreValue;
  if (appValue instanceof Signal) return appValue.peek();
  if (Array.isArray(appValue)) return appValue.map(valueToFirestore);
  if (typeof appValue === 'object') {
    if (objectKey('toFirestore').isMethodIn<FirestoreValue>(appValue)) {
      return appValue.toFirestore();
    }
    return mapValues(appValue, valueToFirestore) as Record<string, FirestoreValue>;
  }
  throw new Error(`Unsupported value type: ${typeof appValue}`);
}
